export default class {

    constructor ($translate, lodash, CLASSIFIER_ENUM) {
        this.$translate = $translate;
        this.lodash = lodash;
        this.CLASSIFIER_ENUM = CLASSIFIER_ENUM;
    }

    setDepartments (departments) {
        this.departments = departments;
    }

    setRoles (roles) {
        this.roles = roles;
    }

    setLevels (levels) {
        this.levels = levels;
    }

    init (classifiers) {
        this.departments = classifiers.filter(classifier => classifier.type === this.CLASSIFIER_ENUM.KEYS.DEPARTMENT);
        this.roles = classifiers.filter(classifier => classifier.type === this.CLASSIFIER_ENUM.KEYS.ROLE);
        this.levels = classifiers.filter(classifier => classifier.type === this.CLASSIFIER_ENUM.KEYS.LEVEL);

        this.initClassifierMultiFilter();
    }

    initClassifierMultiFilter () {
        this.toggleDepartment = this.toggleRole = this.toggleLevel = true;
        this.allDepartmentModel = {
            id: null,
            type: this.CLASSIFIER_ENUM.KEYS.DEPARTMENT,
            label: this.$translate.instant('course.All departments')
        };
        this.allRoleModel = {
            id: null,
            type: this.CLASSIFIER_ENUM.KEYS.ROLE,
            label: this.$translate.instant('course.All roles')
        };
        this.allLevelModel = {
            id: null,
            type: this.CLASSIFIER_ENUM.KEYS.LEVEL,
            label: this.$translate.instant('course.All levels')
        };

        this.departmentSelect = this.getFilter(this.mapModels(this.departments, this.allDepartmentModel, this.CLASSIFIER_ENUM.KEYS.DEPARTMENT));
        this.roleSelect = this.getFilter(this.mapModels(this.roles, this.allRoleModel, this.CLASSIFIER_ENUM.KEYS.ROLE));
        this.levelSelect = this.getFilter(this.mapModels(this.levels, this.allLevelModel, this.CLASSIFIER_ENUM.KEYS.LEVEL));
    }

    /**
     * Maps possible selectbox options to suitable format
     * @param classifier
     * @param defaultModel
     * @param type
     * @returns {*}
     */
    mapModels (classifier, defaultModel, type) {
        classifier = classifier.map((val) => ({
            id: val.id,
            type,
            label: val.value
        }));
        classifier.unshift(defaultModel);
        return classifier;
    }

    /**
     * Return filter used to initialize one multiselect
     * @param values
     * @returns object
     */
    getFilter (values) {
        const settings = {
            checkBoxes: true,
            showCheckAll: false,
            showUncheckAll: false,
            enableSearch: true,
            smartButtonMaxItems: 2,
            selectionLimit: 0,
            idProperty: 'id'
        };

        const events = {
            onItemDeselect: this.getItemCallBack(),
            onItemSelect: this.getItemCallBack(),
            onSelectionChanged: this.getSelectionChangedCallback()
        };
        return { models: [values[0]], options: values, settings, events };
    }

    /**
     * Callback function that is called when item is added or removed from selectbox
     * Callback is called before selectionChanged callback
     * @returns {Function}
     */
    getItemCallBack () {
        return item => {
            const { departmentSelect, roleSelect, levelSelect, CLASSIFIER_ENUM } = this;
            const isAllModel = item.id === null;

            // if allModel model is chosen, set only it as selected model
            if (item.type === CLASSIFIER_ENUM.KEYS.DEPARTMENT && isAllModel) {
                departmentSelect.models = [this.allDepartmentModel];
            }
            if (item.type === CLASSIFIER_ENUM.KEYS.ROLE && isAllModel) {
                roleSelect.models = [this.allRoleModel];
            }
            if (item.type === CLASSIFIER_ENUM.KEYS.LEVEL && isAllModel) {
                levelSelect.models = [this.allLevelModel];
            }

            switch (item.type) {
            case CLASSIFIER_ENUM.KEYS.DEPARTMENT:
                if (!isAllModel) {
                    departmentSelect.models = departmentSelect.models.filter(model => model.id !== null);
                }
                if (departmentSelect.models.length > 1) {
                    this.reRenderMultiSelects(CLASSIFIER_ENUM.KEYS.DEPARTMENT);
                }
                break;
            case CLASSIFIER_ENUM.KEYS.ROLE:
                if (!isAllModel) {
                    roleSelect.models = roleSelect.models.filter(model => model.id !== null);
                }
                if (roleSelect.models.length > 1) {
                    this.reRenderMultiSelects(CLASSIFIER_ENUM.KEYS.ROLE);
                }
                break;
            default:
                if (!isAllModel) {
                    levelSelect.models = levelSelect.models.filter(model => model.id !== null);
                }
                if (levelSelect.models.length > 1) {
                    this.reRenderMultiSelects(CLASSIFIER_ENUM.KEYS.LEVEL);
                }
            }
        };
    }

    /**
     * Re-renders all selectboxes with correct selection limits
     * Selection limit 0 means multiselect while 1 means only single item can be selected
     * @param except string type of selectbox to not rerender
     */
    reRenderMultiSelects (except = null) {
        switch (except) {
        case this.CLASSIFIER_ENUM.KEYS.DEPARTMENT:
            this.reRenderRoleSelect(1);
            this.reRenderLevelSelect(1);
            break;
        case this.CLASSIFIER_ENUM.KEYS.ROLE:
            this.reRenderDepartmentSelect(1);
            this.reRenderLevelSelect(1);
            break;
        case this.CLASSIFIER_ENUM.KEYS.LEVEL:
            this.reRenderDepartmentSelect(1);
            this.reRenderRoleSelect(1);
            break;
        default:
            this.reRenderDepartmentSelect(0);
            this.reRenderRoleSelect(0);
            this.reRenderLevelSelect(0);
        }
    }

    reRenderDepartmentSelect (selectionLimit) {
        if (this.departmentSelect.settings.selectionLimit === selectionLimit) {
            return;
        }
        this.departmentSelect.settings.selectionLimit = selectionLimit;
        this.toggleDepartment = !this.toggleDepartment;
    }

    reRenderRoleSelect (selectionLimit) {
        if (this.roleSelect.settings.selectionLimit === selectionLimit) {
            return;
        }
        this.roleSelect.settings.selectionLimit = selectionLimit;
        this.toggleRole = !this.toggleRole;
    }

    reRenderLevelSelect (selectionLimit) {
        if (this.levelSelect.settings.selectionLimit === selectionLimit) {
            return;
        }
        this.levelSelect.settings.selectionLimit = selectionLimit;
        this.toggleLevel = !this.toggleLevel;
    }

    /**
     * Callback function to call when selectbox selection changes
     * Callback is called after change
     * @returns {Function}
     */
    getSelectionChangedCallback () {
        return () => {
            const { departmentSelect, roleSelect, levelSelect } = this;
            if (!departmentSelect.models.length) {
                departmentSelect.models = [this.allDepartmentModel];
            }
            if (!roleSelect.models.length) {
                roleSelect.models = [this.allRoleModel];
            }
            if (!levelSelect.models.length) {
                levelSelect.models = [this.allLevelModel];
            }

            if (departmentSelect.models.length <= 1
                && roleSelect.models.length <= 1
                && levelSelect.models.length <= 1
            ) {
                this.reRenderMultiSelects();
            }
        };
    }

    getTranslations () {
        return {
            checkAll: this.$translate.instant('filter.Check All'),
            uncheckAll: this.$translate.instant('filter.Uncheck All'),
            buttonDefaultText: this.$translate.instant('filter.Select'),
            searchPlaceholder: this.$translate.instant('filter.Search')
        };
    }
}



// WEBPACK FOOTER //
// ./src/app/services/filter2/index.js